import {H2} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import plur from 'plur'
import {memo, useMemo} from 'react'

import {restApi} from '../../../../../../services/rest'
import type {ChangeId} from '../../../../../../types'
import {UserProperties} from '../../../../../../utils/userProperties'
import ContentPanel from '../../../../../common/ContentPanel/ContentPanel'
import contentPanelStyles from '../../../../../common/ContentPanel/ContentPanel.css'
import {useSequenceLoaderReadyHandler} from '../../../../../common/SequenceLoader/SequenceLoader.hooks'
import type {SeqProps} from '../../../../../common/SequenceLoader/SequenceLoader.types'
import TestsTree from '../../../../Tests/TestsTree/TestsTree'
import type {TestsTreeContextType} from '../../../../Tests/TestsTree/TestsTree.context'
import {TestsTreeContext} from '../../../../Tests/TestsTree/TestsTree.context'
import {
  useFetchTreeData,
  useTestOccurrenceTreeArg,
} from '../../../../Tests/TestsTree/TestsTree.hooks'
import {getRootTestCounters} from '../../../../Tests/TestsTree/TestsTree.utils'
import {getChange} from '../../../Changes.selectors'

import {getChangeTestsTreeLocator} from './ChangeTestsTree.utils'

import styles from './ChangeTestsTree.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = SeqProps & {
  className?: string
  changeId: ChangeId
}

function ChangeTestsTree({changeId}: Props) {
  const personal = useAppSelector(state => getChange(state, changeId)?.personal)

  const treeLocator = getChangeTestsTreeLocator(changeId, personal)
  const arg = useTestOccurrenceTreeArg(treeLocator)
  const {isReady, failed, newFailed} = restApi.endpoints.getTestOccurrenceTree.useQueryState(arg, {
    selectFromResult: ({data}) => {
      const counters = getRootTestCounters(data?.tree.node)
      return {
        isReady: data != null,
        failed: counters?.failed ?? 0,
        newFailed: counters?.newFailed ?? 0,
      }
    },
  })

  useSequenceLoaderReadyHandler(isReady)
  useFetchTreeData(treeLocator)

  const labelId = 'changeTestsTree'
  const renderContent = useMemo(
    () => <TestsTree labelId={labelId} locator={treeLocator} arg={arg} />,
    [treeLocator, arg],
  )

  return failed > 0 ? (
    <ContentPanel
      className={styles.panel}
      heading={
        failed > 0 ? (
          <span id={labelId}>{`${failed} Failed ${plur('Test', failed)}${
            newFailed > 0 ? `, ${newFailed} new` : ''
          }`}</span>
        ) : (
          'Tests'
        )
      }
      panelType={UserProperties.EXPAND_CHANGE_TESTS_SECTION}
      headingClassName={classNames({[contentPanelStyles.errorHeading]: failed > 0})}
      content={renderContent}
    />
  ) : (
    <H2 className={contentPanelStyles.htmlHeading}>{'No failed tests'}</H2>
  )
}

function ChangeTestsTreeWrapper(props: Props) {
  const changeTestsTreeContextValue: TestsTreeContextType = useMemo(
    () => ({
      getEndpoint: locator => `changes/${encodeURIComponent(locator)}/testsTree`,
      locatorInEndpoint: true,
    }),
    [],
  )

  return (
    <TestsTreeContext.Provider value={changeTestsTreeContextValue}>
      <ChangeTestsTree {...props} />
    </TestsTreeContext.Provider>
  )
}

export default memo(ChangeTestsTreeWrapper)
